import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Terminal } from "lucide-react";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const LINES = [
    { text: t("hero.line1"), accent: false },
    { text: t("hero.line2"), accent: true },
    { text: t("hero.line3"), accent: false },
  ];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="https://images.pexels.com/photos/97077/pexels-photo-97077.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt=""
          className="w-full h-full object-cover opacity-[0.07] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white" />
      </motion.div>
      <div className="hero-grid absolute inset-0 -z-10" aria-hidden="true" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10" aria-hidden="true">
        <div className="animate-scanline absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-black/20 to-transparent" />
      </div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 pt-40 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <Terminal className="w-4 h-4 text-accent" />
          <p className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-muted-foreground">
            {t("hero.overline")}
          </p>
        </motion.div>

        <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem]">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45 + i * 0.14, ease: EASE }}
                className={`block ${line.accent ? "text-outline" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="mt-8 max-w-xl text-muted-foreground text-base sm:text-lg leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-book-consultation-btn"
            className="inline-flex items-center gap-2 bg-primary text-background font-bold px-7 py-4 text-sm hover:bg-accent transition-colors duration-300"
          >
            {t("hero.cta1")} <ArrowDownRight className="w-4 h-4" />
          </a>
          <a
            href="#services"
            data-testid="hero-explore-services-btn"
            className="inline-flex items-center gap-2 border border-border text-foreground font-bold px-7 py-4 text-sm hover:border-accent hover:text-accent transition-colors duration-300"
          >
            {t("hero.cta2")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-16 grid grid-cols-3 max-w-lg gap-6 border-t border-black pt-6"
          data-testid="hero-stats"
        >
          {[
            ["07+", t("hero.stat1")],
            ["03", t("hero.stat2")],
            ["05", t("hero.stat3")],
          ].map(([num, label]) => (
            <div key={label}>
              <p className="font-heading font-extrabold text-2xl sm:text-3xl text-primary">{num}</p>
              <p className="font-mono text-[10px] sm:text-xs tracking-[0.15em] uppercase text-muted-foreground mt-1">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
